import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export default function CarePlanComparisonPage() {
  const { recordId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/admin/careplan-records/${recordId}/comparison`).then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [recordId]);

  if (!data) return <div className="mx-auto max-w-5xl p-6 text-sm text-stone-500" data-testid="comparison-loading">Loading comparison…</div>;

  const comp = data.comparison || {};
  const planCodes = new Set((data.plan_tasks || []).map((t) => t.code));
  const latestPoc = (data.pocs || [])[data.pocs.length - 1];
  const pocCodes = new Set(latestPoc?.task_codes || []);
  const matching = (data.plan_tasks || []).filter((t) => pocCodes.has(t.code));
  const planOnly = (data.plan_tasks || []).filter((t) => !pocCodes.has(t.code));
  const pocOnly = (latestPoc?.task_codes || []).filter((cd) => !planCodes.has(cd));

  const TaskRow = ({ t, tone }) => (
    <div className={`rounded-md border p-2.5 ${tone === "amber" ? "border-[#8a5d14]/30 bg-[#8a5d14]/[0.04]" : "border-stone-200 bg-white"}`}
      data-testid={`cmp-task-${t.code}`}>
      <p className="text-xs font-semibold text-stone-800">{t.code} {t.label}</p>
      {(t.frequency || (t.levels || []).length > 0) && (
        <p className="mt-0.5 text-[11px] text-stone-500">
          {t.frequency}{t.frequency && (t.levels || []).length > 0 ? " · " : ""}{(t.levels || []).join(", ")}
        </p>
      )}
      {t.instruction && <p className="mt-1 text-[11px] leading-relaxed text-stone-600">{t.instruction}</p>}
    </div>
  );

  return (
    <div className="mx-auto max-w-5xl space-y-5 p-4 sm:p-6" data-testid="careplan-comparison-page">
      <button onClick={() => navigate("/careplan-records")}
        className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900" data-testid="comparison-back">
        <ArrowLeft size={14} /> Back to Care Plan Records
      </button>

      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight text-stone-900" data-testid="comparison-title">
          Plan vs POC — {data.client_name}
        </h1>
        <p className="mt-1 text-sm text-stone-500">
          Current plan effective {fmtDate(data.plan_effective_date)}
          {latestPoc && <> · latest POC {fmtDate(latestPoc.poc_start_date)} ({latestPoc.filename})</>}
        </p>
      </div>

      {comp.has_plan && comp.has_poc && (
        <div className="rounded-lg border border-stone-200 bg-white p-4" data-testid="comparison-coverage">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-stone-800">{comp.coverage_percent}% task-code match</p>
            <p className="text-xs text-stone-500">{comp.match_count} matching · {comp.plan_count} in plan · {comp.poc_count} on POC</p>
          </div>
          <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-stone-100">
            <div className="h-full bg-primary" style={{ width: `${comp.coverage_percent}%` }} />
          </div>
          <p className="mt-2 text-[11px] text-stone-500">
            POCs often authorize more than the working plan — differences are for your review, not automatic changes.
          </p>
        </div>
      )}
      {(!comp.has_plan || !comp.has_poc) && (
        <div className="rounded-lg border border-stone-200 bg-white p-4 text-sm text-stone-500" data-testid="comparison-missing">
          {!comp.has_poc ? "No POC source document on file for this client." : "No digital task plan to compare yet."}
        </div>
      )}

      {data.latest_poc_snapshot && (
        <div className="rounded-lg border border-stone-200 bg-white p-4" data-testid="comparison-initial-vs-latest">
          <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">Initial POC vs most recent POC</p>
          <p className="mt-1.5 text-xs text-stone-600">
            {data.minimum_viable_source_label} → most recent {fmtDate(data.latest_poc_snapshot.poc_start_date)} ({data.latest_poc_snapshot.task_codes?.length} tasks)
          </p>
          {(data.latest_poc_snapshot.added_vs_initial || []).length > 0 && (
            <p className="mt-1 text-xs text-[#1e8449]"><strong>Added over time:</strong> {data.latest_poc_snapshot.added_vs_initial.join(", ")}</p>
          )}
          {(data.latest_poc_snapshot.removed_vs_initial || []).length > 0 && (
            <p className="mt-1 text-xs text-[#C0392B]"><strong>Dropped over time:</strong> {data.latest_poc_snapshot.removed_vs_initial.join(", ")}</p>
          )}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-stone-500" data-testid="cmp-matching-h">
            Matching ({matching.length})
          </p>
          <div className="space-y-2">{matching.map((t) => <TaskRow key={t.code} t={t} />)}</div>
        </div>
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-stone-500" data-testid="cmp-planonly-h">
            In plan, not on POC ({planOnly.length})
          </p>
          <div className="space-y-2">{planOnly.map((t) => <TaskRow key={t.code} t={t} tone="amber" />)}</div>
        </div>
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-stone-500" data-testid="cmp-poconly-h">
            On POC, not in plan ({pocOnly.length})
          </p>
          <div className="space-y-2">
            {pocOnly.map((cd) => <TaskRow key={cd} t={{ code: cd, label: (data.pocs[0] && "") || cd }} tone="amber" />)}
          </div>
        </div>
      </div>

      {(data.pocs || []).length > 0 && (
        <div className="rounded-lg border border-stone-200 bg-white p-4" data-testid="comparison-poc-history">
          <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">POC history ({data.pocs.length})</p>
          <div className="mt-2 space-y-1">
            {data.pocs.map((p) => (
              <p key={p.id} className="text-xs text-stone-600">
                {fmtDate(p.poc_start_date)}{p.poc_stop_date ? ` – ${fmtDate(p.poc_stop_date)}` : ""} · {p.task_codes.length} tasks · {p.filename}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
